export class VideoControls {
  constructor() {
    this.selectors = {
      container: "[js-video-controls]",
      video: "video",
    };

    // Fetching starts long before the block reaches the viewport, so the source
    // is buffered by the time a scroll animation reveals it. Playback keeps the
    // tighter window — we only want a video running while it is actually seen.
    this.loadOptions = {
      root: null,
      rootMargin: "600px 0px",
      threshold: 0,
    };

    this.playOptions = {
      root: null,
      rootMargin: "100px",
      threshold: 0.5,
    };

    this.observed = new WeakSet();
    this.loadPromises = new WeakMap();

    this.init();
  }

  init() {
    this.initVideoClasses();
    this.observeVideos();

    document.body.addEventListener("click", this.handleEvent.bind(this));
  }

  handleEvent(e) {
    this.handlePausePlay(e);
  }

  observeVideos(root = document) {
    const videoContainers = root.querySelectorAll(this.selectors.container);

    videoContainers.forEach((container) => {
      const video = container.querySelector(this.selectors.video);

      if (!video || this.observed.has(container)) return;

      this.observed.add(container);
      this.observeLoad(container, video);
      this.observePlayback(container, video);
    });
  }

  observeLoad(container, video) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        // One shot: once the source is promoted there is nothing left to watch.
        observer.unobserve(entry.target);
        this.loadVideoSource(video);
      });
    }, this.loadOptions);

    observer.observe(container);
  }

  observePlayback(container, video) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          video.pause();
          container.classList.remove("video-playing");
          return;
        }

        const shouldAutoplay =
          video.getAttribute("autoplay") === "true" || video.getAttribute("autoplay") === "autoplay";

        if (!shouldAutoplay || container.classList.contains("video-manual-pause")) {
          return;
        }

        // Resolves immediately when the load observer already handled it.
        this.loadVideoSource(video).then(() => {
          video.play().catch((e) => {
            console.warn("VideoControls: autoplay was prevented", e);
          });
          container.classList.remove("video-paused");
          container.classList.add("video-playing");
        });
      });
    }, this.playOptions);

    observer.observe(container);
  }

  loadVideoSource(video) {
    // Cached so a later caller awaits the in-flight load instead of racing it.
    const existing = this.loadPromises.get(video);
    if (existing) return existing;

    const promise = new Promise((resolve) => {
      // Check if video sources need to be loaded from data-src
      const sources = video.querySelectorAll("source[data-src]");
      let sourcesLoaded = false;

      sources.forEach((source) => {
        if (source.hasAttribute("data-src")) {
          source.src = source.getAttribute("data-src");
          source.removeAttribute("data-src");
          sourcesLoaded = true;
        }
      });

      if (!sourcesLoaded) {
        // No sources to load, resolve immediately
        resolve();
        return;
      }

      // The markup ships preload="none" so the browser ignores the video until
      // we ask for it. Lifting that before load() is what makes the early fetch
      // actually buffer rather than stopping at metadata.
      video.preload = "auto";
      video.load();

      // Wait for video to be ready to play
      const handleCanPlay = () => {
        video.removeEventListener("canplay", handleCanPlay);
        resolve();
      };

      video.addEventListener("canplay", handleCanPlay);

      // Fallback timeout in case canplay doesn't fire
      setTimeout(() => {
        video.removeEventListener("canplay", handleCanPlay);
        resolve();
      }, 3000);
    });

    this.loadPromises.set(video, promise);
    return promise;
  }

  initVideoClasses() {
    const videoContainers = document.querySelectorAll(this.selectors.container);

    videoContainers.forEach((container) => {
      const video = container.querySelector(this.selectors.video);

      if (video) {
        if (video.paused) {
          container.classList.add("video-paused");
          container.classList.remove("video-playing");
        } else {
          container.classList.add("video-playing");
          container.classList.remove("video-paused");
        }
      }
    });
  }

  handlePausePlay(e) {
    const target = e.target;

    if (target.closest(this.selectors.container)) {
      const container = target.closest(this.selectors.container);

      if (
        container.dataset.customControls !== "true" &&
        container.dataset.customControls !== "auto"
      ) {
        return;
      }

      const video = container.querySelector(this.selectors.video);

      if (video) {
        if (video.paused) {
          this.loadVideoSource(video).then(() => {
            video.play().catch((e) => {
              console.warn("VideoControls: play was prevented", e);
            });
            container.classList.add("video-playing");
            container.classList.remove("video-paused", "video-manual-pause");
          });
        } else {
          video.pause();
          container.classList.add("video-paused", "video-manual-pause");
          container.classList.remove("video-playing");
        }
      }
    }
  }
}

export const videoControls = new VideoControls();
window.VideoControls = videoControls;
